import { useTranslation } from 'react-i18next'
import React, { useContext } from 'react'
import { StageDiveConfig, StageDiveConfigContext } from '../../../../../util/app_initialization/appconfig'
import { useLoginContext } from 'stagedive/LoginContext'
import { toNullable } from 'fp-ts/es6/Option'
import { CardContainer, CloseButton, HelpContainer, PageTitleContainer, StyledLink } from '../focus_view_shared.styles'
import { getManageAccountsURL, getUserManagementURL } from '../ads_console_utils'
import { Alert, CardHeader, Icon, Link, Text } from '@amzn/storm-ui'
import { times } from '@amzn/storm-ui-icons'
import { MetricEvent } from '@amzn/aapn-front-end-metrics'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AlertContainer,
  BoldText,
  FormFieldNameContainer,
  FormFieldValueContainer,
  FormRowContainer,
  InviteDataEntryContainer,
  RoleInfoContainer,
  StyledCard,
} from './bulk_users_invite.styles'
import { AlertType } from '@amzn/storm-ui/dist/types/Alert/types'
import { Invitation } from './bulk_invite_logic'
import * as i18nKeyList from './bulk_users_invite_i18n_mapping'

export const FocusViewPageTitle = (): JSX.Element => {
  const { t } = useTranslation()
  const config = useContext<StageDiveConfig>(StageDiveConfigContext)
  const { loginContext } = useLoginContext()
  const partnerId = toNullable(loginContext.partnerProfileId) ?? ''
  return (
    <PageTitleContainer>
      <CloseButton onClick={() => (window.location.href = getManageAccountsURL(partnerId, config.hostname))}>
        <Icon type={times} size={'lg'} />
      </CloseButton>
      <Text fontSize={'medium'} textColor={'base'} type={'h4'}>
        {t(i18nKeyList.BULK_INVITE_USERS_PAGE_TITLE)}
      </Text>
    </PageTitleContainer>
  )
}
export const FocusViewHelp = (): JSX.Element => {
  const { t } = useTranslation()
  return (
    <HelpContainer>
      <StyledLink
        to="/help"
        aria-label={t(i18nKeyList.BULK_INVITE_USERS_HEADER_HELP_ICON_ARIA_LABEL)}
        data-id={'bulk-invite-help-button'}
        data-publish-metrics={MetricEvent.Click}
      >
        <FontAwesomeIcon icon={{ prefix: 'far', iconName: 'question-circle' }} data-id={'bulk-invite-help-button'} data-publish-metrics={MetricEvent.Click} />
      </StyledLink>
    </HelpContainer>
  )
}

interface BackToUsersLinkProps {
  label?: string
}
export const BackToUsersLink: React.FunctionComponent<BackToUsersLinkProps> = (props: BackToUsersLinkProps): JSX.Element => {
  const { t } = useTranslation()
  const config = useContext<StageDiveConfig>(StageDiveConfigContext)
  const { loginContext } = useLoginContext()
  const partnerId = toNullable(loginContext.partnerProfileId) ?? ''
  return (
    <Text>
      <Link href={getUserManagementURL(partnerId, config.hostname)}>{!!props.label ? props.label : t(i18nKeyList.BULK_INVITE_USERS_BACK_TO_USERS)}</Link>
    </Text>
  )
}
export const UserInviteRoleInfo = (): JSX.Element => {
  const { t } = useTranslation()
  return (
    <FormRowContainer>
      <FormFieldNameContainer>
        <BoldText>{t(i18nKeyList.BULK_INVITE_USERS_USER_PERMISSION_LABEL)}</BoldText>
      </FormFieldNameContainer>
      <FormFieldValueContainer>
        <RoleInfoContainer>
          <Text>{t(i18nKeyList.BULK_INVITE_USERS_USER_INVITE_ROLE)}</Text>
          <Text>{t(i18nKeyList.BULK_INVITE_USERS_USER_INVITE_ROLE_PERMISSIONS)}</Text>
        </RoleInfoContainer>
      </FormFieldValueContainer>
    </FormRowContainer>
  )
}

interface UsersInvitePreviewAndExecutionCardProps {
  alertType: AlertType
  invitations: Invitation[]
}

export const UsersInvitePreviewAndExecutionCard: React.FunctionComponent<UsersInvitePreviewAndExecutionCardProps> = (
  props: UsersInvitePreviewAndExecutionCardProps,
): JSX.Element => {
  const { t } = useTranslation()
  return (
    <CardContainer>
      <StyledCard>
        <CardHeader>
          <Text textColor="base" type="h2">
            {t(i18nKeyList.BULK_INVITE_USERS_CARD_DESCRIPTION)}
          </Text>
        </CardHeader>
        <AlertContainer>
          {props.alertType === 'error' && (
            <Alert type={props.alertType} header={t(i18nKeyList.BULK_INVITE_USERS_INVITE_SEND_ERROR_HEADER, { inviteCount: props.invitations.length })}>
              {t(i18nKeyList.BULK_INVITE_USERS_INVITE_SEND_ERROR_TEXT)}
            </Alert>
          )}
          {props.alertType === 'info' && (
            <Alert type={props.alertType} header={t(i18nKeyList.BULK_INVITE_USERS_PREVIEW_ALERT_HEADER, { inviteCount: props.invitations.length })}>
              {t(i18nKeyList.BULK_INVITE_USERS_PREVIEW_ALERT_TEXT)}
            </Alert>
          )}
        </AlertContainer>
        <InviteDataEntryContainer>
          <FormRowContainer>
            <FormFieldNameContainer>
              <BoldText>{t(i18nKeyList.BULK_INVITE_USERS_EMAIL_LABEL)}</BoldText>
            </FormFieldNameContainer>
            <FormFieldValueContainer>
              {props.invitations.map((invitation: Invitation, index: number) => (
                <Text key={index}>{invitation.email}</Text>
              ))}
            </FormFieldValueContainer>
          </FormRowContainer>
          <UserInviteRoleInfo />
        </InviteDataEntryContainer>
      </StyledCard>
    </CardContainer>
  )
}
